import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import { useSectionAccess } from '../hooks/useSectionAccess';
import {
  getOpsTeam, getOpsAttendance, regularizeOpsAttendance,
  getOpsPendingLeaves, opsLeaveAction, getOpsPerformance,
  getOpsLeaveHistory, getEmployees, assignToOpsTeam, removeFromOpsTeam
} from '../api';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Users, Clock, CalendarCheck, BarChart3, Search,
  CheckCircle2, XCircle, UserPlus, UserMinus, AlertTriangle,
  TrendingUp, TrendingDown, RefreshCw, Calendar, ArrowUpDown
} from 'lucide-react';
import { toast } from 'sonner';

/* ── Team Tab ── */
const TeamTab = ({ team, allEmployees, refreshTeam }) => {
  const [search, setSearch] = useState('');
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignSearch, setAssignSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);
  const [loading, setLoading] = useState(false);

  const teamIds = new Set(team.members?.map(m => m.id) || []);
  const nonTeamEmps = allEmployees.filter(e => !teamIds.has(e.id));

  const filtered = (team.members || []).filter(m => {
    const s = search.toLowerCase();
    return !s || (m.name || '').toLowerCase().includes(s) || (m.employee_id || '').toLowerCase().includes(s);
  });

  const handleAssign = async () => {
    if (!selectedIds.length) return;
    try {
      setLoading(true);
      await assignToOpsTeam(selectedIds);
      toast.success(`${selectedIds.length} member(s) added to team`);
      setAssignOpen(false);
      setSelectedIds([]);
      refreshTeam();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to assign'); }
    finally { setLoading(false); }
  };

  const handleRemove = async (empId) => {
    if (!window.confirm('Remove this member from your team?')) return;
    try {
      await removeFromOpsTeam([empId]);
      toast.success('Member removed from team');
      refreshTeam();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input placeholder="Search team..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9" data-testid="ops-team-search" />
        </div>
        <Button onClick={() => setAssignOpen(true)} className="bg-indigo-600 hover:bg-indigo-700" data-testid="ops-add-member-btn">
          <UserPlus className="h-4 w-4 mr-2" />Add Members
        </Button>
      </div>

      {filtered.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-slate-500"><Users className="h-12 w-12 mx-auto text-slate-300 mb-3" /><p>No team members yet</p><p className="text-sm">Click "Add Members" to build your team</p></CardContent></Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {filtered.map(m => (
            <Card key={m.id} className="hover:shadow-md transition-shadow" data-testid={`team-member-${m.employee_id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="font-semibold text-slate-800">{m.name || `${m.first_name || ''} ${m.last_name || ''}`.trim()}</p>
                    <p className="text-xs text-slate-500">{m.employee_id}</p>
                    {m.email && <p className="text-xs text-sky-600 mt-1">{m.email}</p>}
                    <p className="text-xs text-slate-400 mt-1">{m.designation || ''} {m.department ? `• ${m.department}` : ''}</p>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => handleRemove(m.id)} title="Remove from team" className="text-red-400 hover:text-red-600 hover:bg-red-50">
                    <UserMinus className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={assignOpen} onOpenChange={setAssignOpen}>
        <DialogContent className="max-w-md max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><UserPlus className="h-5 w-5 text-indigo-600" />Add Team Members</DialogTitle><DialogDescription>Select employees to add to your team</DialogDescription></DialogHeader>
          <div className="space-y-3 py-2">
            <Input placeholder="Search employees..." value={assignSearch} onChange={e => setAssignSearch(e.target.value)} />
            <div className="max-h-60 overflow-y-auto border rounded-lg">
              {nonTeamEmps.filter(e => {
                const s = assignSearch.toLowerCase();
                return !s || (e.name || '').toLowerCase().includes(s) || (e.employee_id || '').toLowerCase().includes(s);
              }).slice(0, 30).map(e => (
                <label key={e.id} className="flex items-center gap-3 p-2 hover:bg-slate-50 cursor-pointer border-b last:border-b-0">
                  <input type="checkbox" checked={selectedIds.includes(e.id)} onChange={() => setSelectedIds(prev => prev.includes(e.id) ? prev.filter(x => x !== e.id) : [...prev, e.id])} className="rounded" />
                  <div><p className="text-sm font-medium">{e.name || `${e.first_name || ''} ${e.last_name || ''}`}</p><p className="text-xs text-slate-500">{e.employee_id}</p></div>
                </label>
              ))}
            </div>
            {selectedIds.length > 0 && <p className="text-sm text-indigo-600 font-medium">{selectedIds.length} selected</p>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignOpen(false)}>Cancel</Button>
            <Button onClick={handleAssign} disabled={!selectedIds.length || loading} className="bg-indigo-600 hover:bg-indigo-700" data-testid="ops-confirm-assign">{loading ? 'Adding...' : `Add ${selectedIds.length || ''} Members`}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

/* ── Attendance Tab ── */
const AttendanceTab = ({ team }) => {
  const [attendance, setAttendance] = useState({ records: [], summary: {} });
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [loading, setLoading] = useState(false);
  const [regOpen, setRegOpen] = useState(false);
  const [regRecord, setRegRecord] = useState(null);
  const [regStatus, setRegStatus] = useState('present');
  const [regReason, setRegReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const fetchAtt = useCallback(async () => {
    try {
      setLoading(true);
      const res = await getOpsAttendance({ date });
      setAttendance(res.data);
    } catch { toast.error('Failed to load attendance'); }
    finally { setLoading(false); }
  }, [date]);

  useEffect(() => { fetchAtt(); }, [fetchAtt]);

  const openRegularize = (record) => {
    setRegRecord(record);
    setRegStatus(record.status === 'absent' ? 'present' : record.status);
    setRegReason('');
    setRegOpen(true);
  };

  const handleRegularize = async () => {
    if (!regReason.trim()) { toast.error('Reason is required'); return; }
    try {
      setSubmitting(true);
      await regularizeOpsAttendance({ attendance_id: regRecord.id, new_status: regStatus, reason: regReason });
      toast.success('Attendance regularized');
      setRegOpen(false);
      fetchAtt();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setSubmitting(false); }
  };

  const summary = attendance.summary || {};
  const statusColors = { present: 'bg-green-100 text-green-700', absent: 'bg-red-100 text-red-700', half_day: 'bg-amber-100 text-amber-700', on_leave: 'bg-blue-100 text-blue-700', late: 'bg-orange-100 text-orange-700' };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-4 flex-wrap">
        <div><Label className="text-xs text-slate-500 block mb-1">Date</Label><Input type="date" value={date} onChange={e => setDate(e.target.value)} className="w-44" data-testid="ops-att-date" /></div>
        <Button variant="outline" onClick={fetchAtt} className="mt-5"><RefreshCw className="h-4 w-4 mr-1" />Refresh</Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {[
          { label: 'Team Size', value: attendance.team_size || team.team_size || 0, color: 'bg-slate-100 text-slate-700' },
          { label: 'Present', value: summary.present || 0, color: 'bg-green-100 text-green-700' },
          { label: 'Absent', value: summary.absent || 0, color: 'bg-red-100 text-red-700' },
          { label: 'Half Day', value: summary.half_day || 0, color: 'bg-amber-100 text-amber-700' },
          { label: 'On Leave', value: summary.on_leave || 0, color: 'bg-blue-100 text-blue-700' },
        ].map(s => (
          <Card key={s.label}><CardContent className="p-3 text-center"><p className="text-xs text-slate-500">{s.label}</p><p className={`text-2xl font-bold mt-1 rounded-lg px-2 py-1 ${s.color}`}>{s.value}</p></CardContent></Card>
        ))}
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="py-12 text-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto" /></div>
          ) : attendance.records?.length === 0 ? (
            <div className="py-12 text-center text-slate-500"><Clock className="h-10 w-10 mx-auto text-slate-300 mb-2" /><p>No attendance records for this date</p></div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50"><tr>{['Employee','Status','Check In','Check Out','Hours','Action'].map(h => <th key={h} className="text-left py-3 px-4 text-sm font-medium text-slate-600">{h}</th>)}</tr></thead>
                <tbody>
                  {attendance.records.map((r, i) => (
                    <tr key={r.id || i} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="py-3 px-4"><p className="font-medium text-sm">{r.employee_name}</p><p className="text-xs text-slate-500">{r.employee_code}</p></td>
                      <td className="py-3 px-4"><Badge className={statusColors[r.status] || 'bg-slate-100 text-slate-600'}>{r.status}{r.regularized ? ' (R)' : ''}</Badge></td>
                      <td className="py-3 px-4 text-sm text-slate-600">{r.check_in || '-'}</td>
                      <td className="py-3 px-4 text-sm text-slate-600">{r.check_out || '-'}</td>
                      <td className="py-3 px-4 text-sm text-slate-600">{r.total_hours || '-'}</td>
                      <td className="py-3 px-4">
                        <Button variant="ghost" size="sm" onClick={() => openRegularize(r)} className="text-indigo-600 hover:bg-indigo-50 text-xs" data-testid={`ops-regularize-${i}`}>
                          Regularize
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Regularization Dialog */}
      <Dialog open={regOpen} onOpenChange={setRegOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="text-indigo-600">Regularize Attendance</DialogTitle><DialogDescription>{regRecord?.employee_name} - {date}</DialogDescription></DialogHeader>
          <div className="space-y-3 py-2">
            <div><Label>Current Status</Label><Badge className={statusColors[regRecord?.status] || 'bg-slate-100'}>{regRecord?.status}</Badge></div>
            <div><Label>New Status *</Label>
              <Select value={regStatus} onValueChange={setRegStatus}><SelectTrigger data-testid="ops-reg-status"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="present">Present</SelectItem><SelectItem value="absent">Absent</SelectItem><SelectItem value="half_day">Half Day</SelectItem><SelectItem value="on_leave">On Leave</SelectItem></SelectContent></Select>
            </div>
            <div><Label>Reason *</Label><Textarea value={regReason} onChange={e => setRegReason(e.target.value)} placeholder="Reason for regularization..." rows={2} data-testid="ops-reg-reason" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRegOpen(false)}>Cancel</Button>
            <Button onClick={handleRegularize} disabled={submitting} className="bg-indigo-600 hover:bg-indigo-700" data-testid="ops-reg-submit">{submitting ? 'Saving...' : 'Regularize'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

/* ── Leaves Tab ── */
const LeavesTab = () => {
  const [pending, setPending] = useState({ leaves: [], pending_count: 0 });
  const [loading, setLoading] = useState(true);
  const [actionOpen, setActionOpen] = useState(false);
  const [selectedLeave, setSelectedLeave] = useState(null);
  const [action, setAction] = useState('approved');
  const [remarks, setRemarks] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const fetchLeaves = useCallback(async () => {
    try {
      setLoading(true);
      const res = await getOpsPendingLeaves();
      setPending(res.data);
    } catch { toast.error('Failed to load leaves'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchLeaves(); }, [fetchLeaves]);

  const openAction = (leave, act) => {
    setSelectedLeave(leave);
    setAction(act);
    setRemarks('');
    setActionOpen(true);
  };

  const handleAction = async () => {
    try {
      setSubmitting(true);
      await opsLeaveAction(selectedLeave.id, { action, remarks });
      toast.success(`Leave ${action}`);
      setActionOpen(false);
      fetchLeaves();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setSubmitting(false); }
  };

  const typeColors = { casual: 'bg-sky-100 text-sky-700', sick: 'bg-red-100 text-red-700', earned: 'bg-emerald-100 text-emerald-700', privilege: 'bg-violet-100 text-violet-700', maternity: 'bg-pink-100 text-pink-700', paternity: 'bg-blue-100 text-blue-700' };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2"><AlertTriangle className="h-5 w-5 text-amber-500" /><span className="font-semibold text-slate-700">{pending.pending_count} Pending Request{pending.pending_count !== 1 ? 's' : ''}</span></div>
        <Button variant="outline" size="sm" onClick={fetchLeaves}><RefreshCw className="h-4 w-4 mr-1" />Refresh</Button>
      </div>

      {loading ? (
        <div className="py-12 text-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto" /></div>
      ) : pending.leaves.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-slate-500"><CheckCircle2 className="h-10 w-10 mx-auto text-green-300 mb-2" /><p>No pending leave requests</p><p className="text-sm">All caught up!</p></CardContent></Card>
      ) : (
        <div className="space-y-3">
          {pending.leaves.map(l => (
            <Card key={l.id} className="hover:shadow-md transition-shadow" data-testid={`ops-leave-${l.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="font-semibold text-slate-800">{l.employee_name}</p>
                      <p className="text-xs text-slate-500">({l.employee_code})</p>
                      <Badge className={typeColors[l.leave_type] || 'bg-slate-100 text-slate-700'}>{l.leave_type}</Badge>
                    </div>
                    <p className="text-sm text-slate-600">{l.start_date} to {l.end_date} ({l.total_days || 1} day{(l.total_days || 1) > 1 ? 's' : ''})</p>
                    {l.reason && <p className="text-sm text-slate-500 mt-1">Reason: {l.reason}</p>}
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" onClick={() => openAction(l, 'approved')} className="bg-green-600 hover:bg-green-700" data-testid={`ops-approve-${l.id}`}>
                      <CheckCircle2 className="h-4 w-4 mr-1" />Approve
                    </Button>
                    <Button size="sm" variant="destructive" onClick={() => openAction(l, 'rejected')} data-testid={`ops-reject-${l.id}`}>
                      <XCircle className="h-4 w-4 mr-1" />Reject
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={actionOpen} onOpenChange={setActionOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className={action === 'approved' ? 'text-green-600' : 'text-red-600'}>{action === 'approved' ? 'Approve' : 'Reject'} Leave</DialogTitle><DialogDescription>{selectedLeave?.employee_name} - {selectedLeave?.leave_type} ({selectedLeave?.start_date} to {selectedLeave?.end_date})</DialogDescription></DialogHeader>
          <div className="py-2"><Label>Remarks (Optional)</Label><Textarea value={remarks} onChange={e => setRemarks(e.target.value)} placeholder={action === 'rejected' ? 'Reason for rejection...' : 'Optional remarks...'} rows={2} data-testid="ops-leave-remarks" /></div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setActionOpen(false)}>Cancel</Button>
            <Button onClick={handleAction} disabled={submitting} className={action === 'approved' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'} data-testid="ops-leave-confirm">{submitting ? 'Processing...' : action === 'approved' ? 'Confirm Approve' : 'Confirm Reject'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

/* ── Performance Tab ── */
const PerformanceTab = () => {
  const [perf, setPerf] = useState({ kpis: {}, members: [] });
  const [loading, setLoading] = useState(true);
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [year, setYear] = useState(new Date().getFullYear());
  const [sortKey, setSortKey] = useState('attendance_pct');
  const [sortDir, setSortDir] = useState('desc');
  const [statusFilter, setStatusFilter] = useState('all');

  const fetchPerf = useCallback(async () => {
    try {
      setLoading(true);
      const res = await getOpsPerformance({ month, year });
      setPerf(res.data);
    } catch { toast.error('Failed to load performance data'); }
    finally { setLoading(false); }
  }, [month, year]);

  useEffect(() => { fetchPerf(); }, [fetchPerf]);

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir(d => d === 'desc' ? 'asc' : 'desc');
    else { setSortKey(key); setSortDir('desc'); }
  };

  // Unique status options derived from members
  const statusOptions = React.useMemo(() => {
    const set = new Set((perf.members || []).map(m => m.employment_status || 'Working'));
    return ['all', ...Array.from(set)];
  }, [perf.members]);

  const filtered = (perf.members || []).filter(m => {
    if (statusFilter === 'all') return true;
    return (m.employment_status || 'Working') === statusFilter;
  });

  const sorted = [...filtered].sort((a, b) => {
    const mul = sortDir === 'desc' ? -1 : 1;
    return (a[sortKey] - b[sortKey]) * mul;
  });

  const kpis = perf.kpis || {};
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-4 flex-wrap">
        <div><Label className="text-xs text-slate-500 block mb-1">Month</Label>
          <Select value={String(month)} onValueChange={v => setMonth(Number(v))}><SelectTrigger className="w-32" data-testid="ops-perf-month"><SelectValue /></SelectTrigger><SelectContent>{months.map((m, i) => <SelectItem key={i+1} value={String(i+1)}>{m}</SelectItem>)}</SelectContent></Select>
        </div>
        <div><Label className="text-xs text-slate-500 block mb-1">Year</Label><Input type="number" value={year} onChange={e => setYear(Number(e.target.value))} className="w-24" /></div>
        <div><Label className="text-xs text-slate-500 block mb-1">Status</Label>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-40" data-testid="ops-perf-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              {statusOptions.map(s => (
                <SelectItem key={s} value={s} data-testid={`ops-perf-status-${s}`}>
                  {s === 'all' ? 'All Status' : s}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Button variant="outline" onClick={fetchPerf} className="mt-5"><RefreshCw className="h-4 w-4 mr-1" />Refresh</Button>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
        {[
          { label: 'Team Size', value: kpis.team_size, icon: Users, color: 'text-slate-600', bg: 'bg-slate-100' },
          { label: 'Working Days', value: kpis.working_days, icon: CalendarCheck, color: 'text-blue-600', bg: 'bg-blue-100' },
          { label: 'Avg Attendance', value: `${kpis.avg_attendance_pct || 0}%`, icon: CalendarCheck, color: 'text-green-600', bg: 'bg-green-100' },
          { label: 'Avg Punctuality', value: `${kpis.avg_punctuality_pct || 0}%`, icon: Clock, color: 'text-indigo-600', bg: 'bg-indigo-100' },
          { label: 'Late Arrivals', value: kpis.total_late_arrivals, icon: AlertTriangle, color: 'text-amber-600', bg: 'bg-amber-100' },
          { label: 'Perfect Attendance', value: kpis.perfect_attendance, icon: CheckCircle2, color: 'text-emerald-600', bg: 'bg-emerald-100' },
          { label: 'Chronic Absentees', value: kpis.chronic_absentees, icon: XCircle, color: 'text-red-600', bg: 'bg-red-100' },
        ].map(k => {
          const Icon = k.icon;
          return (
            <Card key={k.label}><CardContent className="p-3"><div className="flex items-center gap-2"><div className={`w-8 h-8 ${k.bg} rounded-lg flex items-center justify-center`}><Icon className={`h-4 w-4 ${k.color}`} /></div><div><p className="text-xs text-slate-500">{k.label}</p><p className="text-lg font-bold text-slate-800">{k.value ?? 0}</p></div></div></CardContent></Card>
          );
        })}
      </div>

      {/* Performance Table */}
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-lg flex items-center gap-2"><BarChart3 className="h-5 w-5 text-indigo-600" />Member Performance - {months[month-1]} {year}</CardTitle></CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="py-12 text-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto" /></div>
          ) : sorted.length === 0 ? (
            <div className="py-12 text-center text-slate-500"><BarChart3 className="h-10 w-10 mx-auto text-slate-300 mb-2" /><p>No performance data for this period</p></div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Employee</th>
                    <th className="text-center py-3 px-3 text-sm font-medium text-slate-600">Status</th>
                    {[
                      { key: 'working_days', label: 'Working Days' },
                      { key: 'present_days', label: 'Present' },
                      { key: 'absent_days', label: 'Absent' },
                      { key: 'half_days', label: 'Half Day' },
                      { key: 'late_arrivals', label: 'Late' },
                      { key: 'leave_days_taken', label: 'Leaves' },
                      { key: 'attendance_pct', label: 'Attendance %' },
                      { key: 'punctuality_pct', label: 'Punctuality %' },
                    ].map(col => (
                      <th key={col.key} className="text-center py-3 px-3 text-sm font-medium text-slate-600 cursor-pointer hover:text-indigo-600" onClick={() => toggleSort(col.key)}>
                        <div className="flex items-center justify-center gap-1">{col.label}<ArrowUpDown className="h-3 w-3" /></div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sorted.map((m, i) => {
                    const attColor = m.attendance_pct >= 90 ? 'text-green-600' : m.attendance_pct >= 75 ? 'text-amber-600' : 'text-red-600';
                    const punctColor = m.punctuality_pct >= 90 ? 'text-green-600' : m.punctuality_pct >= 75 ? 'text-amber-600' : 'text-red-600';
                    const status = m.employment_status || 'Working';
                    const isActive = ['Working', 'working', 'Active', 'active'].includes(status);
                    const statusClass = isActive ? 'bg-green-100 text-green-700' : 'bg-slate-200 text-slate-600';
                    return (
                      <tr key={m.employee_id || i} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="py-3 px-4"><p className="font-medium text-sm">{m.name}</p><p className="text-xs text-slate-500">{m.employee_code}</p></td>
                        <td className="py-3 px-3 text-center"><span className={`px-2 py-0.5 rounded text-xs font-medium ${statusClass}`} data-testid={`emp-status-${m.employee_id}`}>{status}</span></td>
                        <td className="py-3 px-3 text-center text-sm text-blue-600 font-medium" data-testid={`emp-working-days-${m.employee_id}`}>{m.working_days ?? 0}</td>
                        <td className="py-3 px-3 text-center text-sm text-green-600 font-medium">{m.present_days}</td>
                        <td className="py-3 px-3 text-center text-sm text-red-600 font-medium">{m.absent_days}</td>
                        <td className="py-3 px-3 text-center text-sm text-amber-600 font-medium">{m.half_days}</td>
                        <td className="py-3 px-3 text-center text-sm text-orange-600 font-medium">{m.late_arrivals}</td>
                        <td className="py-3 px-3 text-center text-sm">{m.leave_days_taken}</td>
                        <td className="py-3 px-3 text-center"><span className={`font-bold ${attColor}`}>{m.attendance_pct}%</span>{m.attendance_pct >= 90 ? <TrendingUp className="h-3 w-3 inline ml-1 text-green-500" /> : m.attendance_pct < 75 ? <TrendingDown className="h-3 w-3 inline ml-1 text-red-500" /> : null}</td>
                        <td className="py-3 px-3 text-center"><span className={`font-bold ${punctColor}`}>{m.punctuality_pct}%</span></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

/* ── Main Page ── */
const OpsManagerDashboard = ({ user, setUser }) => {
  const [team, setTeam] = useState({ team_size: 0, members: [] });
  const [allEmployees, setAllEmployees] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [loading, setLoading] = useState(true);

  const fetchTeam = useCallback(async () => {
    try {
      const res = await getOpsTeam();
      setTeam(res.data);
    } catch {}
  }, []);

  const fetchAll = useCallback(async () => {
    try {
      setLoading(true);
      const [teamRes, empRes, leavesRes] = await Promise.allSettled([
        getOpsTeam(),
        getEmployees(),
        getOpsPendingLeaves()
      ]);
      if (teamRes.status === 'fulfilled') setTeam(teamRes.value.data);
      if (empRes.status === 'fulfilled') setAllEmployees(empRes.value.data || []);
      if (leavesRes.status === 'fulfilled') setPendingCount(leavesRes.value.data?.pending_count || 0);
    } catch {}
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const hasAccess = useSectionAccess('/ops-dashboard');
  if (hasAccess === false) {
    return (
      <Layout user={user} setUser={setUser}>
        <div className="flex flex-col items-center justify-center h-96">
          <Users className="h-16 w-16 text-red-400 mb-4" />
          <h2 className="text-xl font-semibold text-slate-700">Access Denied</h2>
          <p className="text-slate-500 mt-2">You don't have permission to view this page.</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} setUser={setUser}>
      <div className="space-y-6" data-testid="ops-manager-dashboard">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Ops Manager Dashboard</h1>
          <p className="text-gray-500 mt-1">Manage team attendance, leaves, and performance</p>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
          {[
            { label: 'Team Members', value: team.team_size, icon: Users, color: 'text-indigo-600', bg: 'bg-indigo-100' },
            { label: 'Pending Leaves', value: pendingCount, icon: AlertTriangle, color: pendingCount > 0 ? 'text-amber-600' : 'text-green-600', bg: pendingCount > 0 ? 'bg-amber-100' : 'bg-green-100' },
            { label: 'Today', value: new Date().toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }), icon: Calendar, color: 'text-sky-600', bg: 'bg-sky-100' },
            { label: 'Manager', value: team.manager_name || user?.full_name || '-', icon: Users, color: 'text-slate-600', bg: 'bg-slate-100', isText: true },
          ].map(s => {
            const Icon = s.icon;
            return (
              <Card key={s.label}><CardContent className="p-4"><div className="flex items-center gap-3"><div className={`w-10 h-10 ${s.bg} rounded-lg flex items-center justify-center`}><Icon className={`h-5 w-5 ${s.color}`} /></div><div><p className="text-sm text-slate-500">{s.label}</p>{s.isText ? <p className="text-sm font-semibold text-slate-800 truncate max-w-[150px]">{s.value}</p> : <p className="text-2xl font-bold text-slate-900">{s.value}</p>}</div></div></CardContent></Card>
            );
          })}
        </div>

        <Tabs defaultValue="team" className="w-full">
          <TabsList className="grid w-full max-w-lg grid-cols-4">
            <TabsTrigger value="team" data-testid="ops-tab-team"><Users className="h-4 w-4 mr-1" />Team</TabsTrigger>
            <TabsTrigger value="attendance" data-testid="ops-tab-attendance"><Clock className="h-4 w-4 mr-1" />Attendance</TabsTrigger>
            <TabsTrigger value="leaves" data-testid="ops-tab-leaves"><CalendarCheck className="h-4 w-4 mr-1" />Leaves {pendingCount > 0 && <Badge className="ml-1 bg-red-500 text-white text-xs px-1.5">{pendingCount}</Badge>}</TabsTrigger>
            <TabsTrigger value="performance" data-testid="ops-tab-performance"><BarChart3 className="h-4 w-4 mr-1" />Performance</TabsTrigger>
          </TabsList>

          <TabsContent value="team" className="mt-4">
            {loading ? <div className="py-12 text-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto" /></div> : <TeamTab team={team} allEmployees={allEmployees} refreshTeam={fetchTeam} />}
          </TabsContent>
          <TabsContent value="attendance" className="mt-4"><AttendanceTab team={team} /></TabsContent>
          <TabsContent value="leaves" className="mt-4"><LeavesTab /></TabsContent>
          <TabsContent value="performance" className="mt-4"><PerformanceTab /></TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
};

export default OpsManagerDashboard;
